import { Link } from "react-router-dom";
import { ChefHat, Utensils, UserRound, PartyPopper, ArrowUpRight, MessageCircle, Phone, Shield, Clock, Award, Sparkles, Smile, CheckCircle2, XCircle, Heart } from "lucide-react";
import { BRAND, WHATSAPP_LINK, waLink } from "../brand";
import { Seo } from "../components/Seo";

const HERO_IMG = "/brand/hero-portrait.png";
const VEG_IMG = "/brand/chopping-action.png";
const COOK_IMG = "/brand/team-host-kitchen.png";
const TEAM_IMG = "/brand/cooking-stove.png";
const DAY_IMG = "/brand/day-with-ooloo.png";

const SERVICES = [
  { icon: Utensils, title: "Kitchen Prep", desc: "Chopping, cutting, pre-preparation." },
  { icon: ChefHat, title: "Cooking", desc: "North Indian, multi-cuisine & more." },
  { icon: UserRound, title: "On-Demand Cook", desc: "Professional cook at your convenience." },
  { icon: PartyPopper, title: "Family Gathering & Party", desc: "Up to 20 people. 2 trained cooks." },
];

const WHY_US = [
  { icon: Shield, t: "Verified & Background Checked Staff" },
  { icon: Award, t: "Trained Kitchen Professionals" },
  { icon: Sparkles, t: "Hygienic Kitchen Practices" },
  { icon: CheckCircle2, t: "Covered & Insured" },
  { icon: Clock, t: "On-Time Service" },
  { icon: Smile, t: "Customer Satisfaction Guaranteed" },
];

const STEPS = [
  { n: "01", t: "Book & Relax", d: "Book your kitchen partner on call or WhatsApp." },
  { n: "02", t: "Verified Cook Arrives on Time", d: "Trained & verified cook arrives on time." },
  { n: "03", t: "We Handle the Kitchen", d: "Focus on your guests or rest while we cook." },
];

const Home = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BRAND.siteUrl}/#business`,
    name: "ooloo — your daily kitchen partner",
    image: `${BRAND.siteUrl}/brand/hero-portrait.png`,
    url: BRAND.siteUrl,
    telephone: `+${BRAND.whatsappCountry}${BRAND.whatsappNumberRaw}`,
    email: BRAND.email,
    priceRange: "₹₹",
    description: "On-demand kitchen support at your doorstep in 30 minutes across Lucknow. Trained cooks, kitchen prep, party support and monthly plans.",
    address: { "@type": "PostalAddress", addressLocality: "Lucknow", addressRegion: "Uttar Pradesh", addressCountry: "IN" },
    areaServed: BRAND.zones.map((z) => ({ "@type": "Place", name: `${z}, Lucknow` })),
    openingHoursSpecification: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], opens: "07:00", closes: "21:00" },
    sameAs: [BRAND.instagramUrl, BRAND.facebookUrl, `https://wa.me/${BRAND.whatsappCountry}${BRAND.whatsappNumberRaw}`],
  };

  return (
    <main data-testid="home-page" className="bg-[#f7f5f2]">
      <Seo
        title="ooloo — Need Help in the Kitchen Today? On-Demand Cooks in Lucknow"
        description="Trained cooks, trusted support, hassle-free. Book kitchen prep, cooking or on-demand cook in Lucknow — at your doorstep in 30 minutes. From ₹299/hour."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-12">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6 rise">
            <p className="eyebrow flex items-center gap-2">
              <span className="inline-block w-8 h-px bg-[#5c5a56]" />
              Lucknow&apos;s Hyperlocal Startup
            </p>
            <h1 className="font-display mt-6 text-[2.75rem] sm:text-[3.75rem] md:text-[5.5rem] lg:text-[6.5rem] font-black tracking-tighter leading-[0.92] text-[#0a0a0a]">
              Need help in the
              <br />
              kitchen{" "}
              <span className="italic font-serif font-light text-[#c45b3e]">today?</span>
            </h1>
            <p className="mt-6 font-serif italic text-2xl md:text-3xl text-[#c45b3e]">
              We&apos;ve got you. <Heart size={24} className="inline fill-[#c45b3e] stroke-[#c45b3e]" />
            </p>
            <p className="mt-6 max-w-xl text-lg md:text-xl text-[#5c5a56] leading-relaxed font-medium">
              Trained cooks. Trusted support. Hassle-free experience. Book a cook on WhatsApp — at your doorstep in 30 minutes across Lucknow.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 rise rise-2">
              <a href={waLink("Hi ooloo, I need kitchen help today.")} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-whatsapp">
                <MessageCircle size={18} strokeWidth={2.5} /> Book on WhatsApp
              </a>
              <a href={`tel:+${BRAND.whatsappCountry}${BRAND.whatsappNumberRaw}`} data-testid="hero-order-btn" className="btn-primary">
                <Phone size={16} /> Call {BRAND.phoneDisplay}
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md rise rise-3">
              {[
                { k: "30 min", v: "At your doorstep" },
                { k: "2,400+", v: "Homes served" },
                { k: "100%", v: "Hygiene tested" },
              ].map((s) => (
                <div key={s.k} data-testid={`stat-${s.k.replace(/\s+/g, "-").toLowerCase()}`}>
                  <p className="font-display text-3xl md:text-4xl font-black tracking-tighter text-[#0a0a0a]">{s.k}</p>
                  <p className="mt-1 text-xs text-[#5c5a56] font-medium tracking-wide">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 rise rise-2">
            <div className="relative">
              <img src={HERO_IMG} alt="ooloo kitchen executive — your daily kitchen partner" className="w-full h-[460px] md:h-[640px] object-cover object-center rounded-[2rem] shadow-2xl shadow-black/15" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-[#e5e2dc] p-5 max-w-[260px] shadow-xl shadow-black/5">
                <p className="eyebrow text-[#0a0a0a]">Serving Lucknow</p>
                <p className="mt-2 font-display text-base font-bold leading-snug text-[#0a0a0a]">{BRAND.zones.join(" · ")}</p>
              </div>
              <div className="absolute -top-5 -right-5 bg-[#0a0a0a] text-white rounded-2xl px-5 py-3 shadow-lg flex items-center gap-2">
                <Clock size={18} strokeWidth={2.5} className="text-[#c45b3e]" />
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-white/70">Available in</p>
                  <p className="font-display text-lg font-black tracking-tight">30 minutes*</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#e5e2dc] bg-[#0a0a0a] text-white py-5 overflow-hidden">
        <div className="flex gap-10 animate-[marquee_28s_linear_infinite] whitespace-nowrap">
          {Array.from({ length: 2 }).flatMap((_, i) =>
            ["Kitchen Prep", "Cooking", "On-Demand Cook", "Family & Party", "30 min · at your doorstep", "Smart Subscriptions"].map((w, idx) => (
              <span key={`${i}-${idx}`} className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-white/80">
                {w} <span className="text-[#c45b3e]">·</span>
              </span>
            )),
          )}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="eyebrow">What we do</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
              Everything except
              <br />
              <span className="italic font-serif font-light text-[#c45b3e]">the eating</span>.
            </h2>
          </div>
          <Link to="/services" data-testid="home-services-cta" className="btn-primary self-start md:self-auto">
            All services <ArrowUpRight size={18} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.title} data-testid={`home-service-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`} className="bento-card p-6 hover:-translate-y-1 transition">
                <div className="w-12 h-12 rounded-full bg-[#efebe6] flex items-center justify-center text-[#c45b3e]">
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight text-[#0a0a0a]">{s.title}</h3>
                <p className="mt-2 text-[#5c5a56]">{s.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Exclusion note */}
        <div className="mt-10 accent-card border-2 border-dashed border-[#c45b3e]/30 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <XCircle size={22} className="text-[#c45b3e] mt-1 flex-shrink-0" />
            <div>
              <p className="font-display font-extrabold text-lg text-[#0a0a0a]">Please note</p>
              <p className="text-[#5c5a56] text-sm">We do <strong>not</strong> provide kitchen cleaning, utensil cleaning or serving. Our cooks focus on cooking your meal — beautifully.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING BAND */}
      <section className="bg-[#0a0a0a] text-white py-20 grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow text-[#c45b3e]">Flexible hourly booking</p>
              <h2 className="font-display mt-4 text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]">
                Pay only for
                <br />
                <span className="italic font-serif font-light text-[#c45b3e]">what you cook</span>.
              </h2>
              <a href={waLink("Hi ooloo, I want to book a cook now.")} target="_blank" rel="noreferrer" data-testid="pricing-whatsapp" className="btn-whatsapp mt-8">
                <MessageCircle size={18} strokeWidth={2.5} /> Book in 30 min
              </a>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { hr: "1 Hour", price: "₹299", cap: "Up to 3 people" },
                { hr: "2 Hours", price: "₹399", cap: "Up to 6 people", badge: "Popular" },
                { hr: "Extra 30 min", price: "₹99", cap: "Add anytime" },
              ].map((p) => (
                <div key={p.hr} data-testid={`pricing-${p.hr.toLowerCase().replace(/\s+/g, "-")}`} className="relative bg-white text-[#0a0a0a] rounded-2xl p-6 text-center">
                  {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c45b3e] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">{p.badge}</span>}
                  <p className="text-xs font-bold tracking-widest uppercase text-[#5c5a56]">{p.hr}</p>
                  <p className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter text-[#c45b3e]">{p.price}</p>
                  <p className="mt-2 text-xs text-[#5c5a56]">{p.cap}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAMILY GATHERING & PARTY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="accent-card p-8 md:p-12 grain relative overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow"><PartyPopper size={12} className="inline mr-1" /> Family gathering & party</p>
              <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
                Hosting up to
                <br />
                <span className="italic font-serif font-light text-[#c45b3e]">20 people?</span>
              </h2>
              <p className="mt-6 max-w-md text-lg text-[#0a0a0a] leading-relaxed font-medium">
                Two trained cooks handle prep and cooking end-to-end. You focus on your guests — we make sure nobody leaves hungry.
              </p>
              <a href={waLink("Hi ooloo, I want to book cooks for a party.")} target="_blank" rel="noreferrer" data-testid="party-cta" className="btn-whatsapp mt-8">
                <MessageCircle size={18} strokeWidth={2.5} /> Book cooks for party
              </a>
            </div>
            <div className="lg:col-span-5 bg-[#0a0a0a] text-white rounded-3xl p-8">
              <p className="text-xs font-bold tracking-widest uppercase text-white/60">Party rate</p>
              <p className="mt-2 font-display text-7xl font-black tracking-tighter text-[#c45b3e]">₹999<span className="text-2xl text-white/80">/hr</span></p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#c45b3e]" /> 2 trained cooks</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#c45b3e]" /> Up to 20 guests</li>
                <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#c45b3e]" /> Minimum 3 hours booking</li>
              </ul>
              <p className="mt-6 text-[11px] text-white/50 uppercase tracking-widest font-bold">Minimum 3 hour booking is mandatory for party</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3-STEP PROCESS */}
      <section className="bg-[#efebe6] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">The ooloo experience</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
              3 easy <span className="italic font-serif font-light text-[#c45b3e]">steps</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`step-${i + 1}`} className="bg-white rounded-3xl p-8 border border-[#e5e2dc] relative">
                <p className="font-display text-6xl font-black tracking-tighter text-[#c45b3e]/20 absolute top-4 right-6">{s.n}</p>
                <p className="font-mono text-xs font-bold tracking-widest uppercase text-[#c45b3e]">Step {s.n}</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-[#0a0a0a]">{s.t}</h3>
                <p className="mt-3 text-[#5c5a56]">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            {BRAND.exclusions.map((x) => (
              <div key={x} className="flex items-center gap-2 bg-white border border-[#e5e2dc] rounded-full px-4 py-3 justify-center">
                <XCircle size={16} className="text-[#c45b3e]" />
                <span className="font-display text-sm font-bold tracking-tight text-[#0a0a0a]">{x}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET TEAM GALLERY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow">Meet your ooloo team</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
              Trained. Trusted.
              <br />
              <span className="italic font-serif font-light text-[#c45b3e]">Tied-in-apron</span>.
            </h2>
          </div>
          <p className="text-[#5c5a56] max-w-md text-lg leading-relaxed">
            Every ooloo cook wears the black apron, passes a strict background check, and is trained on hygiene SOPs before stepping into your home.
          </p>
        </div>
        <div className="grid md:grid-cols-12 gap-4 md:gap-5 auto-rows-[minmax(180px,auto)]">
          <div className="bento-card md:col-span-7 md:row-span-2 p-0 overflow-hidden relative">
            <img src={COOK_IMG} alt="ooloo kitchen team cooking together" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative h-full p-8 md:p-10 flex flex-col justify-end bg-gradient-to-t from-black/80 via-black/10 to-transparent text-white">
              <p className="eyebrow text-white/80">In the kitchen</p>
              <h3 className="mt-3 font-display text-3xl md:text-4xl font-extrabold tracking-tight max-w-md">Real cooks, your stove, your taste.</h3>
            </div>
          </div>
          <div className="bento-card md:col-span-5 p-0 overflow-hidden relative min-h-[300px]">
            <img src={TEAM_IMG} alt="ooloo cook at the stove" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="bento-card md:col-span-5 p-0 overflow-hidden relative min-h-[300px]">
            <img src={VEG_IMG} alt="ooloo cook chopping vegetables" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-[#e5e2dc]">
              <p className="font-display text-sm font-extrabold tracking-tight text-[#0a0a0a]">Your kitchen · your ingredients · your taste</p>
            </div>
          </div>
        </div>
      </section>

      {/* A DAY WITH OOLOO */}
      <section className="bg-[#efebe6] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="eyebrow">A day with ooloo</p>
              <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
                Chop. Cook.
                <br />
                <span className="italic font-serif font-light text-[#c45b3e]">Done.</span>
              </h2>
              <p className="mt-6 text-lg text-[#5c5a56] leading-relaxed max-w-md">
                From the first slice of onion to the last dish plated, your ooloo cook handles the kitchen — while you sit back, sip chai, and enjoy your home.
              </p>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative">
                <img src={DAY_IMG} alt="A day with ooloo" className="w-full h-auto rounded-[2rem] shadow-2xl shadow-black/15" />
                <div className="absolute -bottom-5 -right-5 bg-[#0a0a0a] text-white rounded-2xl px-5 py-4 max-w-[220px] shadow-xl">
                  <p className="font-mono text-xs font-bold text-[#c45b3e]">00:00</p>
                  <p className="mt-1 font-display text-sm font-extrabold tracking-tight leading-tight">Kitchen help in no time</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow">Why choose ooloo</p>
          <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
            Built on <span className="italic font-serif font-light text-[#c45b3e]">trust</span>.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {WHY_US.map((w) => {
            const Icon = w.icon;
            return (
              <div key={w.t} data-testid={`why-${w.t.toLowerCase().split(" ")[0]}`} className="bento-card p-6 flex flex-col items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#efebe6] flex items-center justify-center text-[#c45b3e]">
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <p className="font-display text-base md:text-lg font-extrabold tracking-tight text-[#0a0a0a] leading-snug">{w.t}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <p className="eyebrow">Loved in Lucknow</p>
        <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a] max-w-3xl">
          Families across Lucknow trust ooloo.
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[
            { q: "I haven't chopped an onion in three months. ooloo just handles it.", n: "Anjali T.", r: "Gomti Nagar" },
            { q: "The cook is polite, on time, and my kids love the taste. Perfect.", n: "Sandeep K.", r: "Gomti Nagar Extension" },
            { q: "Party for 15, done in 4 hours. Two cooks, zero stress. Loved it.", n: "Ritika & Aman", r: "Sushant Golf City" },
          ].map((t, i) => (
            <figure key={i} data-testid={`testimonial-${i}`} className="bento-card p-8">
              <p className="text-3xl text-[#c45b3e] font-serif">&ldquo;</p>
              <blockquote className="mt-2 font-display text-xl font-bold leading-snug tracking-tight text-[#0a0a0a]">{t.q}</blockquote>
              <figcaption className="mt-6 text-sm">
                <span className="font-bold text-[#0a0a0a]">{t.n}</span>
                <span className="text-[#5c5a56]"> · {t.r}, Lucknow</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 0% STRESS BAND */}
      <section className="bg-[#c45b3e] py-20 grain text-white relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="font-serif italic text-2xl md:text-3xl text-white/90">{BRAND.slogan}</p>
            <h2 className="font-display mt-4 text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9]">
              0% kitchen stress.
              <br />
              <span className="text-white/80">100% home food.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="home-stress-cta" className="inline-flex items-center gap-2 bg-white text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white px-8 py-5 rounded-full font-bold text-lg transition">
              <MessageCircle size={20} strokeWidth={2.5} /> Book on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
